import _ from "lodash";
import { useContext, useState } from "react";

import { IconButton } from "../../../components/common/IconButton";
import LegoAdminPageContext from "../../legoAdminPageContext";
import MultiButtonSwitch from "../../../components/common/editors/MultiButtonSwitch";
import JsonTextEditor from "../../../components/common/editors/JsonTextEditor";
import SingleTextEditor from "../../../components/common/editors/SingleTextEditor";
import SchemaPathInput from "./SchemaPathInput";
import SingleValueSelector from "../../../components/common/editors/SingleValueSelector";

const operators = [
    { label: '=', value: '$eq', placeholder: '"VALUE"' },
    { label: '!=', value: '$ne', placeholder: '"VALUE"' },
    { label: 'is one of', value: '$in', placeholder: '["VALUE1", "VALUE2", ...]' },
    { label: <>is not&nbsp; one of</>, value: '$nin', placeholder: '["VALUE1", "VALUE2", ...]' },
    { label: 'exists', value: '$exists', placeholder: 'true / false' },
    { label: 'must have keys', value: '$hasKeys', placeholder: '$currentFullPath --> ["KEY1", "KEY2", ...]' },
    { label: 'is descendant of', value: '$isDescendantOf', placeholder: '$currentFullPath --> ["PATH1", "PATH2", ...]' },
];

const mongoToRules = (mongoMatch) => {
    if (_.isObject(mongoMatch) && !_.isEmpty(mongoMatch)) {
        return _.toPairs(mongoMatch).map(([path, value]) => {
            if (value.$in) {
                return [path, '$in', value.$in];
            } else if (value.$ne) {
                return [path, '$ne', value.$ne];
            } else if (value.$nin) {
                return [path, '$nin', value.$nin];
            } else if (value.$exists) {
                return [path, '$exists', value.$exists];
            } else if (value.$hasKeys) {
                return [path, '$hasKeys', value.$hasKeys];
            } else if (value.$isDescendantOf) {
                return [path, '$isDescendantOf', value.$isDescendantOf];
            } else {
                return [path, '$eq', value];
            }
        });
    } else {
        return [];
    }
}

const rulesToMongo = (rules) => {
    return _.fromPairs(rules.map(([path, operator, value]) => {
        if (operator === '$eq') {
            return [path, value];
        } else {
            return [path, { [operator]: value }];
        }
    }));
}

function RequiredModal({ required, schema, inheritedRequired, onDone }) {
    const [rules, setRules] = useState(mongoToRules(required));
    const [isRequired, setIsRequired] = useState(required ? true : required);

    const addRule = (newValue = ['', '$eq', null]) => {
        setRules([...rules, newValue]);
    };

    const updateRule = (index, path, operator, value) => {
        const newRules = [...rules];
        newRules[index] = [path, operator, value];
        setRules(newRules);
    };

    const presets = {
        'Only Pickup': ["commercial.bodyType", "$eq", "pickup"],
        'Only ICE/HEV/PHEV': ['powertrain.type', "$in", ["ICE", "HEV", "PHEV"]],
        'Only full EV': ['powertrain.type', "$eq", "EV"],
        'Only ICE': ['powertrain.type', "$eq", "ICE"],        
    }

    return <div style={{ minWidth: '70vw' }} className="text-center">
        <MultiButtonSwitch
            optionColorMap={{
                true: ' bg-primary text-white',
                false: ' bg-ladrillo text-white',
                undefined: 'secondary'
            }}
            options={[
                { label: 'Required', value: true },
                { label: 'Optional', value: false },
                { label: 'Inherit parent', value: undefined }
            ]}
            value={isRequired}
            onChange={(v) => setIsRequired(v)}
        />

        <div className={`mt-2 border bg-light${rules.length ? '-violeta' : ''} p-2 rounded mb-2 ${isRequired ? '' : 'disabled-zone translucent'}`}>
            Special rules - Required ONLY IF:
            <div className="mt-2">
                {rules.map(([path, operator, value], index) => {
                    return <div>
                        <div key={index} className="d-flex align-items-start" style={{gap: '5px'}}>
                            <SchemaPathInput schema={schema} value={path} onChange={(v) => updateRule(index, v, operator, value)} />

                            <div className="flex-shrink-0 zoom-75 text-left" style={{width: '200px'}}>
                                <SingleValueSelector options={operators} value={operator} onChange={(v) => updateRule(index, path, v, value)} />
                            </div>
                            
                            <JsonTextEditor inline placeholder={'eg. ' +_.find(operators, {value: operator})?.placeholder || '...'} 
                            value={value} onChange={(v) => updateRule(index, path, operator, v)} className="flex-grow-1 zoom-90" />

                            <IconButton icon="delete" small level="danger" className="ml-2" onClick={() => setRules(rules.filter((_, i) => i !== index))} />
                        </div>
                        <div className="text-muted small text-right mr-5 mb-1">
                            Eg. {_.find(operators, {value: operator})?.placeholder}
                        </div>
                    </div>
                })}

                <IconButton icon="add" small level="primary" onClick={() => addRule()}>Add special rule </IconButton>

                <div className="mt-2 small">Presets:
                    {_.map(presets, (value, label) => <span key={label} className="btn btn-xs btn-secondary ml-1" onClick={() => addRule(_.cloneDeep(value))}>{label}</span>)}
                </div>
            </div>
        </div>

        <div>
            <IconButton
                icon={'save'}
                fill
                level="primary"
                onClick={() => {
                    let val = isRequired;
                    if (val && rules.length > 0) {
                        val = rulesToMongo(rules);
                    }
                    onDone(val);
                }}
            >
                Save require configuration
            </IconButton>
        </div>
    </div>
}

export default function ToggleRequiredSpec({ required, schema, inheritedRequired, onChange }) {
    const { page } = useContext(LegoAdminPageContext);
    const effectiveRequired = required ?? inheritedRequired;

    const toggleRequired = () => {
        let value = required === true ? false : (required === false ? undefined : true);
        onChange(value);
    };

    const hasRules = _.isObject(required);
    const effectiveHasRules = _.isObject(effectiveRequired);

    const onClick = e => {
        if (e.ctrlKey || e.metaKey || hasRules) {
            page.openModal(
                <RequiredModal
                    required={required}
                    inheritedRequired={inheritedRequired}
                    schema={schema}
                    onDone={(v) => {
                        onChange(v);
                        page.closeModal();
                    }}
                />,
                { title: 'Required configuration', minSize: false, overflow: false }
            );
        } else {
            toggleRequired();
        }
    }


    const icon = required ? (hasRules ? 'security' : 'verified_user') : (required === false ? 'remove_moderator' : 'privacy_tip');

    const inheritedTitle = inheritedRequired ? (effectiveHasRules ? 'required if rules match' : 'required') : (inheritedRequired === false ? 'optional' : 'undefined');
    const title = required ? (hasRules ? 'Required if rules match' : 'Required') : (required === false ? 'Optional' : 'Inherited ' + inheritedTitle);

    const level = effectiveRequired ? (effectiveHasRules ? 'violeta' : 'primary') : (effectiveRequired === false ? 'ladrillo' : 'secondary');

    return <IconButton
        small
        level={level}
        className={required === undefined ? 'translucent' : ''}
        icon={icon}
        title={title}
        onClick={onClick}
    />
}